import React from 'react'
import {
  usePagination,
  useResizeColumns,
  useRowSelect,
  useRowState,
  useSortBy,
  useTable
} from 'react-table'
import { computeTWidth, manipulateHooks, stateReducer } from '..'
import { Flex, Span, styled } from '../../main'
import NoData from '../components/NoData'
import Pagination from '../components/Pagination'
import THead from '../components/THead'
import TableActions from '../containers/TableActions'
import { TableStyle } from '../styled'
import { ITable } from '../types'
import DynamicPagination from '../components/DynamicPagination'

const defaultPageOptions = [10, 25, 50, 100, 150, 200]

const CustomTableStyle = styled(TableStyle)`
  table {
    tr {
      background: rgb(22, 26, 36);
    }
  }
`

function Table(props: ITable) {
  const {
    data = [],
    dataLoading,
    columns,
    className,
    style,
    size = 'm',
    fontSize = 'm',
    refresh,
    refreshing,
    onPageChange,
    defaultPageSize,
    defaultPageIndex,
    onPageSizeChange,
    disablePageSizeChange = false,
    defaultSelectedRowIds,
    getTrProps,
    getTdProps,
    showRowSelect,
    setSelectedRows,
    downloadCSV,
    downloadLabel,
    downloadLoading = false,
    expandable,
    subComponent,
    totalCountText,
    sortable,
    totalElements,
    showTotalCount = true,
    onSortedChange,
    resizableColumns = true,
    commentingModule,
    issueLabel,
    manualPagination = false,
    showPagination = true,
    defaultPageSizeOptions = defaultPageOptions,
    paginationWrapper
  } = props

  var ins: any = useTable(
    {
      data,
      columns,
      stateReducer: (ns, a, ps) =>
        stateReducer(ns, a, ps, {
          defaultPageSize,
          manualPagination,
          defaultPageIndex,
          defaultPageSizeOptions,
          defaultSelectedRowIds
        })
    },
    useSortBy,
    useRowState,
    usePagination,
    useRowSelect,
    useResizeColumns,
    hooks => manipulateHooks({ hooks, showRowSelect, expandable })
  )
  const {
    rows = [],
    page,
    gotoPage,
    nextPage,
    pageCount,
    allColumns,
    canNextPage,
    pageOptions,
    setPageSize,
    headerGroups,
    previousPage,
    getTableProps,
    canPreviousPage,
    selectedFlatRows,
    isAllRowsSelected,
    isAllPageRowsSelected,
    getTableBodyProps,
    totalColumnsWidth,
    totalColumnsMaxWidth,
    totalColumnsMinWidth,
    state: { pageIndex, pageSize, selectedRowIds },
    prepareRow
  } = ins

  React.useEffect(() => {
    if (!showPagination && pageSize !== data.length) {
      setPageSize(data.length)
    } else {
      setPageSize(defaultPageSize || defaultPageSizeOptions[0])
    }
  }, [showPagination, defaultPageSize])

  React.useEffect(() => {
    setSelectedRows?.({
      isAllPageRowsSelected,
      isAllRowsSelected,
      selectedRowIds: Object.keys(selectedRowIds),
      selectedFlatRows
    })
  }, [isAllRowsSelected, selectedRowIds])

  const renderData = showPagination ? page : rows
  const hasDataForCurrPageIndex = showPagination
    ? pageIndex * pageSize < data.length
    : !!renderData.length
  const tableMaxWidth = computeTWidth(headerGroups)
  const totalDataLength = totalElements || data.length

  const dynamicPaginationContainer = () => (
    <Flex alignItemsCenter justifyContentFlexEnd>
      <Span>{dataLoading && totalDataLength ? 'Loading...' : null}</Span>
      <DynamicPagination
        gotoPage={(pageNo: number) => {
          gotoPage(pageNo)
          onPageChange?.(pageNo)
        }}
        pageSize={pageSize}
        canNextPage={canNextPage}
        setPageSize={(changedPageSize: number) => {
          setPageSize(changedPageSize)
          onPageChange?.(0)
          onPageSizeChange?.(changedPageSize)
        }}
        dataLoading={dataLoading}
        disablePageSizeChange={disablePageSizeChange}
        totalElements={totalDataLength}
        pageOptions={defaultPageSizeOptions}
        showPagination={showPagination}
        canPreviousPage={canPreviousPage}
        pageIndex={pageIndex}
      />
    </Flex>
  )
  const pagination = paginationWrapper({
    children: dynamicPaginationContainer()
  })
  return (
    <CustomTableStyle
      data-testid="table"
      size={size}
      style={{ ...(style?.wrapper || {}) }}
      fontSize={fontSize}
      className={className}
    >
      <TableActions
        refresh={refresh}
        refreshing={refreshing}
        downloadCSV={downloadCSV}
        downloadLabel={downloadLabel}
        showTotalCount={showTotalCount}
        totalCountText={totalCountText}
        totalElements={totalDataLength}
        downloadLoading={downloadLoading}
        commentingModule={commentingModule}
        issueLabel={issueLabel}
      />
      {pagination}
      <div className="responsive-table" style={{ minHeight: '28vh' }}>
        <table
          {...getTableProps()}
          style={{ ...getTableProps().style, ...(style?.table || {}) }}
        >
          <THead
            style={style}
            sortable={sortable}
            resizableColumns={resizableColumns}
            onSortedChange={onSortedChange}
            headerGroups={headerGroups}
          />
          <tbody {...getTableBodyProps()}>
            {hasDataForCurrPageIndex ? (
              renderData.map((row: any, trIndex: number) => {
                prepareRow(row)
                const trProps = {
                  ...row.getRowProps(),
                  ...(getTrProps ? getTrProps(row) : {})
                }
                var showSubComp = !!subComponent
                if (expandable && subComponent) {
                  showSubComp = Object.keys(selectedRowIds).includes(row.id)
                }
                return (
                  <React.Fragment key={trIndex}>
                    <tr data-testid={`tbody-tr-${trIndex}`} {...trProps}>
                      {row.cells.map((cell: any, j: number) => {
                        const tdProps = {
                          ['data-testid']: `tbody-td-${trIndex}-${j}`,
                          ...cell.getCellProps(),
                          ...(getTdProps ? getTdProps(cell) : {})
                        }
                        return (
                          <td key={j} {...tdProps}>
                            {cell.render('Cell')}
                          </td>
                        )
                      })}
                    </tr>
                    {showSubComp && (
                      <tr
                        key={trIndex}
                        className="sub-comp"
                        data-testid={`tbody-tr-sub-${trIndex}`}
                      >
                        <td
                          data-testid={`tbody-subcomp-${trIndex}`}
                          colSpan={allColumns.length}
                          style={{ borderBottom: '8px solid #f0f2f5' }}
                        >
                          {subComponent?.({
                            index: row.index,
                            original: row.original,
                            row,
                            level: row.depth,
                            page: pageIndex,
                            pageSize
                          })}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                )
              })
            ) : (
              <NoData
                noDataLoading={
                  dataLoading || (!hasDataForCurrPageIndex && !!refreshing)
                }
                colSpan={allColumns.length}
                style={{ backgroundColor: 'rgb(22, 26, 36)'}}
                border={{border: '0px'}}
              />
            )}
          </tbody>
        </table>
      </div>
      <Flex alignItemsCenter justifyContentFlexEnd>
        <Span>{dataLoading && totalDataLength ? 'Loading...' : null}</Span>
      </Flex>
    </CustomTableStyle>
  )
}

export default Table
